import { useCallback, useMemo } from 'react'

import { formatPrice } from 'modules/billing/lib/f25Prices'
import type { ApiDiscount, DiscountCalculation } from 'modules/upsell/lib/selfUpsell/discountTypes'
import { calculateDiscountAmount } from 'modules/upsell/lib/selfUpsell/discountUtils'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type SelfUpsellPricesProps = {
  addon: Plan
  discounts: ApiDiscount[] | undefined
  crossSellingAddons: Plan[] | undefined
  mandatoryCrossSellingAddons?: Plan[] | undefined
}

const useSelfUpsellPrices = ({
  addon,
  discounts,
  crossSellingAddons,
  mandatoryCrossSellingAddons,
}: SelfUpsellPricesProps) => {
  const {
    computeTotalPrice,
    computeCrossSellingAddonPrice: _computeCrossSellingAddonPrice,
    period,
    currency,
    seats,
    currentSeats,
    discount,
    configPlan,
    isExpenses,
  } = useSelfUpsellContext()

  const variant = useMemo(() => {
    if (!configPlan.variant) return undefined
    return addon.getSpecificVariant(configPlan.variant)
  }, [configPlan.variant, addon])

  const perSeatPrice = variant?.pricing.getSeatsPrice(period)
  const feePrice = variant?.pricing.getFeePrice(period)
  const stairstepPrice = variant?.pricing.getStairstepPrice(
    period,
    isExpenses() ? seats : currentSeats
  )
  const tieredPrice = variant?.pricing.getTieredPrice(period, seats)

  const baseTotalPrice = computeTotalPrice()

  // Helper: Check if addon is in configPlan.addon (source of truth for price calculations)
  // Installation status is already handled - if installed, addon won't be in configPlan.addon
  const isAddonInConfigPlan = useCallback(
    (addonPlan: Plan): boolean => {
      if (!configPlan.addon) return false
      const planId = addonPlan.pricing.getPlanId(period) || ''
      return configPlan.addon.includes(planId)
    },
    [configPlan.addon, period]
  )

  // Helper: Check if addon is selected (either isSelected or in configPlan.addon)
  // Used for discount processing which may need to check both
  const isAddonSelected = useCallback(
    (addonPlan: Plan): boolean => {
      if (addonPlan.isSelected) return true
      return isAddonInConfigPlan(addonPlan)
    },
    [isAddonInConfigPlan]
  )

  // Calculate cross-selling total price from addons in configPlan.addon only
  const crossSellingTotalPrice = useMemo(() => {
    if (!crossSellingAddons) return 0
    return crossSellingAddons.reduce((acc, cur) => {
      if (isAddonInConfigPlan(cur)) {
        const price = cur.pricing.getTotalPrice(period, currentSeats) || 0
        return acc + price
      }
      return acc
    }, 0)
  }, [crossSellingAddons, period, currentSeats, isAddonInConfigPlan])

  // Calculate mandatory cross-selling total price from addons in configPlan.addon only
  const mandatoryCrossSellingTotalPrice = useMemo(() => {
    if (!mandatoryCrossSellingAddons) return 0
    return mandatoryCrossSellingAddons.reduce((acc, cur) => {
      if (isAddonInConfigPlan(cur)) {
        const price = cur.pricing.getTotalPrice(period, currentSeats) || 0
        return acc + price
      }
      return acc
    }, 0)
  }, [mandatoryCrossSellingAddons, period, currentSeats, isAddonInConfigPlan])

  const annualFeePrice = variant?.pricing.getAnnualFeePrice(period)

  /**
   * Processes discounts and creates a map of all discounted addons with their discount info.
   * This allows displaying discounts inline with addon names.
   *
   * Handles three types of discounts:
   * 1. Main addon discounts (lineItemId matches main addon)
   * 2. Direct cross-selling discounts (lineItemId matches a selected cross-selling addon)
   * 3. Cross-selling trigger discounts (discount applies to one addon when another is selected)
   */
  const { discountedAddons, recalculatedDiscounts } = useMemo(() => {
    const discountsList: DiscountCalculation[] = []
    const discountedAddonsMap: Map<
      string,
      { addon: Plan; discount: ApiDiscount; originalPrice: number; discountedPrice: number }
    > = new Map()
    const mainAddonPlanId = addon.pricing.getPlanId(period)

    const processDiscount = (apiDiscount: ApiDiscount, targetAddon: Plan, totalPrice: number) => {
      const planId = targetAddon.pricing.getPlanId(period) || ''
      const discountAmount = calculateDiscountAmount(totalPrice, apiDiscount.discountPercentage)
      const discountedPrice = totalPrice - discountAmount

      // Store addon with discount info for inline display
      discountedAddonsMap.set(planId, {
        addon: targetAddon,
        discount: apiDiscount,
        originalPrice: totalPrice,
        discountedPrice,
      })

      // Also add to discounts list for total calculation
      discountsList.push({
        discountPercentage: apiDiscount.discountPercentage,
        amount: discountAmount,
        discountType: apiDiscount.discountType,
        lineItemId: apiDiscount.lineItemId,
      })
    }

    ;(discounts ?? []).forEach((apiDiscount) => {
      if (!apiDiscount.lineItemId) return

      // 1. Check if discount applies to main addon
      if (apiDiscount.lineItemId === mainAddonPlanId) {
        const totalPrice =
          variant?.pricing.getTotalPrice(period, isExpenses() ? seats : currentSeats) || 0
        processDiscount(apiDiscount, addon, totalPrice)
        return
      }

      // 2. Check for direct cross-selling discount (lineItemId matches a selected addon)
      const allAddonsToCheck = [
        ...(crossSellingAddons || []),
        ...(mandatoryCrossSellingAddons || []),
      ]
      const directAddon = allAddonsToCheck.find(
        (crossSellingAddon) =>
          crossSellingAddon.pricing.getPlanId(period) === apiDiscount.lineItemId &&
          isAddonSelected(crossSellingAddon)
      )

      if (directAddon) {
        const totalPrice = directAddon.pricing.getTotalPrice(period, currentSeats)
        processDiscount(apiDiscount, directAddon, totalPrice)
        return
      }

      // 3. Check for cross-selling trigger discount
      // (e.g., Trainings discount when LMS is selected - discount's lineItemId is LMS)
      const triggerAddon = allAddonsToCheck.find(
        (crossSellingAddon) =>
          crossSellingAddon.pricing.getPlanId(period) === apiDiscount.lineItemId
      )

      if (triggerAddon && isAddonSelected(triggerAddon) && allAddonsToCheck.length > 0) {
        // Find the target addon (different from trigger, receives the discount)
        const targetAddon = allAddonsToCheck.find(
          (addonItem) =>
            isAddonSelected(addonItem) &&
            addonItem.pricing.getPlanId(period) !== apiDiscount.lineItemId
        )

        if (targetAddon) {
          const totalPrice = targetAddon.pricing.getTotalPrice(period, currentSeats)
          processDiscount(
            { ...apiDiscount, lineItemId: targetAddon.pricing.getPlanId(period) },
            targetAddon,
            totalPrice
          )
        }
      }
    })

    return {
      discountedAddons: discountedAddonsMap,
      recalculatedDiscounts: discountsList,
    }
  }, [
    discounts,
    crossSellingAddons,
    mandatoryCrossSellingAddons,
    period,
    currentSeats,
    addon,
    variant,
    seats,
    isExpenses,
    isAddonSelected,
  ])
  // Calculate manual discount amount
  const manualDiscountAmount =
    discount && discount.percentage > 0 ? baseTotalPrice * (discount.percentage / 100) : 0

  // Calculate total discount amount
  const totalDiscountAmount = recalculatedDiscounts.reduce(
    (sum: number, d: { amount: number }) => sum + d.amount,
    0
  )

  const totalPriceAfterDiscount =
    baseTotalPrice + crossSellingTotalPrice + mandatoryCrossSellingTotalPrice - totalDiscountAmount

  const formattedTotalPrice = formatPrice(totalPriceAfterDiscount, currency)

  return {
    perSeatPrice,
    feePrice,
    stairstepPrice,
    tieredPrice,
    baseTotalPrice,
    discountedAddons,
    recalculatedDiscounts,
    manualDiscountAmount,
    totalDiscountAmount,
    totalPriceAfterDiscount,
    formattedTotalPrice,
    annualFeePrice,
  }
}

export default useSelfUpsellPrices
